import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import { setAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { LogIn, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authAPI.login({ email, password });
      setAuth(res.data.token, res.data.user);
      toast.success('Login realizado com sucesso!');
      navigate(res.data.user.role === 'admin' ? '/admin' : '/client');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0F1620] items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[rgba(0,255,163,0.05)] to-transparent" />
        <div className="relative z-10 text-center px-12">
          <img src="/logo-recupnet.png" alt="RecupNet" className="h-16 mx-auto mb-4" />
          <p className="text-white/60 text-lg max-w-md">
            Plataforma de Regularizacao de Credito. Seguranca e tecnologia a servico da sua recuperacao financeira.
          </p>
          <div className="mt-12 grid grid-cols-3 gap-6 max-w-sm mx-auto">
            <div className="text-center">
              <p className="font-display text-2xl font-bold text-[var(--rn-green)]">100%</p>
              <p className="text-xs text-white/40 mt-1">Digital</p>
            </div>
            <div className="text-center">
              <p className="font-display text-2xl font-bold text-[var(--rn-green)]">24/7</p>
              <p className="text-xs text-white/40 mt-1">Disponivel</p>
            </div>
            <div className="text-center">
              <p className="font-display text-2xl font-bold text-[var(--rn-green)]">Seguro</p>
              <p className="text-xs text-white/40 mt-1">Protegido</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <img src="/logo-recupnet.png" alt="RecupNet" className="h-12 mx-auto" />
          </div>
          <h2 className="font-display text-2xl font-semibold mb-2">Acesse sua conta</h2>
          <p className="text-white/50 text-sm mb-8">Entre com seu email e senha para continuar.</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm text-white/70 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                data-testid="login-email-input"
                className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/35 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[rgba(0,255,163,0.30)] focus:ring-offset-2 focus:ring-offset-[#0B0F14]"
              />
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-1.5">Senha</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Sua senha"
                  required
                  data-testid="login-password-input"
                  className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/35 rounded-xl px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-[rgba(0,255,163,0.30)] focus:ring-offset-2 focus:ring-offset-[#0B0F14]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-white/40 hover:text-white/70 transition-colors"
                  data-testid="login-toggle-password"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-form-submit-button"
              className="w-full btn-primary flex items-center justify-center gap-2 py-3 disabled:opacity-50"
            >
              {loading ? <span className="rn-spinner" /> : <><LogIn size={18} /> Entrar</>}
            </button>
          </form>

          <p className="text-center text-sm text-white/50 mt-6">
            Nao tem conta?{' '}
            <Link to="/register" className="text-[var(--rn-green)] hover:underline" data-testid="login-register-link">
              Criar conta
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
